// Channel model + helpers for the gateway (HolyKel) channels overview.
// Channels are grouped by TYPE (kind), never by name, because names differ per gateway.

export const CHANNEL_GROUPS = {
  tanks: { key: "tanks", label: "Tanks", icon: "fa-solid fa-gas-pump" },
  environment: { key: "environment", label: "Environment", icon: "fa-solid fa-temperature-half" },
  pressure: { key: "pressure", label: "Pressure", icon: "fa-solid fa-gauge-high" },
  diagnostics: { key: "diagnostics", label: "Diagnostics", icon: "fa-solid fa-car-battery" },
  other: { key: "other", label: "Other", icon: "fa-solid fa-microchip" },
};

export const GROUP_ORDER = ["tanks", "environment", "pressure", "diagnostics", "other"];

export const CHANNEL_KINDS = {
  level: { label: "Tank level", group: "tanks", icon: "fa-solid fa-fill-drip", defaultUnit: "%" },
  pressure: { label: "Pressure", group: "pressure", icon: "fa-solid fa-gauge-high", defaultUnit: "psi" },
  temperature: { label: "Temperature", group: "environment", icon: "fa-solid fa-temperature-half", defaultUnit: "°F" },
  humidity: { label: "Humidity", group: "environment", icon: "fa-solid fa-droplet", defaultUnit: "%RH" },
  diagnostic: { label: "Diagnostic", group: "diagnostics", icon: "fa-solid fa-car-battery", defaultUnit: "" },
  other: { label: "Sensor", group: "other", icon: "fa-solid fa-microchip", defaultUnit: "" },
};

// Backend `state.channel_type` (or channels_list.channel_type) -> kind.
// Add new backend channel types here.
export const CHANNEL_TYPE_ALIASES = {
  percent_full: "level",
  level: "level",
  tank_level: "level",
  fill_level: "level",
  volume: "level",
  ultrasonic: "level",
  pressure: "pressure",
  psi: "pressure",
  temperature: "temperature",
  temp: "temperature",
  pt100: "temperature",
  rtd: "temperature",
  thermocouple: "temperature",
  humidity: "humidity",
  relative_humidity: "humidity",
  rh: "humidity",
  vehicle_diagnostic: "diagnostic",
  diagnostic: "diagnostic",
};

// threshold_type_id values that are always "alarm" severity (existing pillbar treats 1 and 26 as red).
export const ALARM_THRESHOLD_TYPE_IDS = [1, 26];

// DeviceParameter.parameter_name values that compare against fluid height instead of the channel value.
export const HEIGHT_PARAMETERS = ["product_level", "measured_height", "actual_fluid_height"];

export const STATUS_TONES = { alarm: "error", warning: "warning", ok: "success", nodata: "secondary" };

const STATUS_RANK = { alarm: 3, warning: 2, ok: 1, nodata: 0 };

const OPERATORS = { 1: "=", 2: "≠", 3: "<", 4: "≤", 5: ">", 6: "≥" };

const SUPPLY_NAME_RE = /fresh|potable|supply|fuel|clean|propane|diesel|gas(oline)?\b|def\b/i;

// ---------------------------------------------------------------- readers

export const toNumber = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "number" ? v : parseFloat(String(v).replace(/[^0-9.eE+-]/g, ""));
  return Number.isFinite(n) ? n : null;
};

export const toTimestamp = (v) => {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "number") return v < 1e12 ? v * 1000 : v;
  const t = Date.parse(v);
  return Number.isFinite(t) ? t : null;
};

// Accepts an array of channels or an object keyed by channel number.
export const toChannelEntries = (channels) => {
  if (!channels) return [];
  if (Array.isArray(channels)) {
    return channels
      .filter(Boolean)
      .map((c, i) => [String(c.channel_number ?? c.channel ?? i + 1), c]);
  }
  if (typeof channels === "object") {
    return Object.entries(channels)
      .filter(([, c]) => c && typeof c === "object")
      .map(([k, c]) => [String(c.channel_number ?? k), c]);
  }
  return [];
};

const hasLevelFields = (raw) =>
  raw &&
  ["percent_level", "fill_percentage", "volume_level", "usable_height", "actual_fluid_height"].some(
    (f) => raw[f] !== undefined && raw[f] !== null
  );

export const resolveChannelKind = (raw = {}, override) => {
  const alias = (t) => (t ? CHANNEL_TYPE_ALIASES[String(t).toLowerCase().trim()] : undefined);

  const fromOverride = alias(override);
  if (fromOverride) return fromOverride;

  const fromState = alias(raw?.state?.channel_type) || alias(raw?.channel_type);
  if (fromState) return fromState;

  const unit = String(raw?.state?.unit || raw?.display_unit || raw?.canonical_unit || "")
    .toLowerCase()
    .trim();
  if (/^°?\s?[fc]$|celsius|fahrenheit|deg/.test(unit)) return "temperature";
  if (/%\s?rh|rh%|humid/.test(unit)) return "humidity";
  if (/psi|bar|kpa|mbar|inh2o/.test(unit)) return "pressure";

  if (hasLevelFields(raw)) return "level";

  const key = String(raw?.measurement_key || "").toLowerCase();
  if (/temp/.test(key)) return "temperature";
  if (/humid|\brh\b/.test(key)) return "humidity";
  if (/press/.test(key)) return "pressure";
  if (/level|tank|fill|volume/.test(key)) return "level";
  if (/diag|battery|voltage|obd|engine/.test(key)) return "diagnostic";

  return "other";
};

export const readChannelValue = (raw, kind) => {
  if (!raw) return null;
  if (kind === "level") {
    return toNumber(raw.current_value) ?? toNumber(raw.percent_level) ?? toNumber(raw.fill_percentage);
  }
  return toNumber(raw.display_value) ?? toNumber(raw.current_value);
};

export const readChannelUnit = (raw, kind) => {
  if (kind === "level") return "%";
  return raw?.state?.unit || raw?.display_unit || raw?.canonical_unit || CHANNEL_KINDS[kind]?.defaultUnit || "";
};

// ------------------------------------------------------------- thresholds

export const normalizeThresholds = (deviceThresholds = []) =>
  (deviceThresholds || [])
    .filter((t) => t && t.channel_number !== undefined && t.channel_number !== null)
    .map((t) => {
      const opId = Number(t.comparison_operator_id);
      const typeName = String(t?.DeviceMonitorThresholdType?.threshold_name || "");
      const paramName = String(t?.DeviceParameter?.parameter_name || "");
      const severity =
        ALARM_THRESHOLD_TYPE_IDS.includes(Number(t.threshold_type_id)) ||
        /crit|alarm|emerg|danger/i.test(typeName)
          ? "alarm"
          : "warning";
      return {
        id: t.id,
        channelKey: String(t.channel_number),
        value: toNumber(t.threshold_value),
        operatorId: opId,
        operator: t?.ComparisonOperator?.label || OPERATORS[opId] || "",
        direction: opId === 5 || opId === 6 ? "high" : opId === 3 || opId === 4 ? "low" : "equal",
        severity,
        subject: HEIGHT_PARAMETERS.includes(paramName) ? "height" : "value",
        label: t?.DeviceParameter?.label || typeName || "Threshold",
        typeName,
        raw: t,
      };
    })
    .filter((t) => t.value !== null);

const compare = (v, opId, target) => {
  switch (opId) {
    case 1: return v === target;
    case 2: return v !== target;
    case 3: return v < target;
    case 4: return v <= target;
    case 5: return v > target;
    case 6: return v >= target;
    default: return false;
  }
};

export const isThresholdCrossed = (t, { value, fluidHeight }) => {
  const v = t.subject === "height" ? fluidHeight : value;
  if (v === null || v === undefined) return false;
  return compare(v, t.operatorId, t.value);
};

export const evaluateChannelStatus = ({ value, fluidHeight, thresholds = [], activeAlerts = [] }) => {
  if (value === null || value === undefined) {
    return { level: "nodata", label: "No data", threshold: null };
  }
  const crossed = thresholds
    .filter((t) => isThresholdCrossed(t, { value, fluidHeight }))
    .sort((a, b) => STATUS_RANK[b.severity] - STATUS_RANK[a.severity]);

  if (crossed.length) {
    const t = crossed[0];
    const dir = t.direction === "high" ? "high" : t.direction === "low" ? "low" : "threshold";
    const label = t.severity === "alarm" ? `Critical ${dir}` : dir.charAt(0).toUpperCase() + dir.slice(1);
    return { level: t.severity, label, threshold: t };
  }
  if (activeAlerts && activeAlerts.length) {
    return { level: "warning", label: "Active alert", threshold: null };
  }
  return { level: "ok", label: "Normal", threshold: null };
};

export const needsAttention = (c) => c?.status?.level === "alarm" || c?.status?.level === "warning";

// Supply tanks (fresh/fuel) are bad when LOW, collection tanks (grey/black) are bad when HIGH.
export const resolveTankRole = ({ name, thresholds = [], override }) => {
  if (override === "supply" || override === "collection") return override;
  const hasLow = thresholds.some((t) => t.direction === "low");
  const hasHigh = thresholds.some((t) => t.direction === "high");
  if (hasLow && !hasHigh) return "supply";
  if (hasHigh && !hasLow) return "collection";
  return SUPPLY_NAME_RE.test(String(name || "")) ? "supply" : "collection";
};

// ---------------------------------------------------------------- history

const rowChannels = (row) => row?.value?.channels || row?.data?.channels || row?.channels || null;

export const buildHistoryIndex = (paramValues = [], kindForKey = () => "other") => {
  const index = {};
  (paramValues || []).forEach((row) => {
    const t = toTimestamp(row?.created_at ?? row?.timestamp);
    if (t === null) return;
    toChannelEntries(rowChannels(row)).forEach(([key, raw]) => {
      const v = readChannelValue(raw, kindForKey(key, raw));
      if (v === null) return;
      (index[key] = index[key] || []).push({ t, v });
    });
  });
  Object.values(index).forEach((pts) => pts.sort((a, b) => a.t - b.t));
  return index;
};

export const windowPoints = (points = [], hours, now = Date.now()) => {
  const from = now - hours * 3600e3;
  return points.filter((p) => p.t >= from);
};

export const seriesStats = (points = []) => {
  if (!points.length) return null;
  let min = Infinity;
  let max = -Infinity;
  let sum = 0;
  points.forEach(({ v }) => {
    if (v < min) min = v;
    if (v > max) max = v;
    sum += v;
  });
  return {
    min,
    max,
    avg: sum / points.length,
    count: points.length,
    first: points[0],
    last: points[points.length - 1],
  };
};

// Linear 24h rate projected to the nearest threshold the value is moving toward.
// status: none | insufficient | eta | crossed | flat
export const forecastNextThreshold = ({ value, points24 = [], thresholds = [] }) => {
  const valueThresholds = thresholds.filter((t) => t.subject === "value" && t.direction !== "equal");
  if (!valueThresholds.length) return { status: "none" };
  if (value === null || value === undefined) return { status: "insufficient" };

  const anyCrossed = valueThresholds.some((t) => isThresholdCrossed(t, { value }));
  const first = points24[0];
  const last = points24[points24.length - 1];
  const spanH = first && last ? (last.t - first.t) / 3600e3 : 0;
  if (points24.length < 2 || spanH < 1) {
    return anyCrossed ? { status: "crossed" } : { status: "insufficient" };
  }

  const rate = (last.v - first.v) / spanH; // units per hour
  const candidates = valueThresholds
    .filter((t) => !isThresholdCrossed(t, { value }))
    .filter((t) => (t.direction === "high" ? rate > 0 && t.value > value : rate < 0 && t.value < value))
    .map((t) => ({ threshold: t, hours: (t.value - value) / rate }))
    .filter((c) => Number.isFinite(c.hours) && c.hours >= 0)
    .sort((a, b) => a.hours - b.hours);

  if (candidates.length) return { status: "eta", rate, ...candidates[0] };
  if (anyCrossed) return { status: "crossed", rate };
  return { status: "flat", rate };
};

// ------------------------------------------------------------------ zones

export const zoneKeyFor = (name, override) => {
  if (override) return String(override).toLowerCase().trim();
  const key = String(name || "")
    .toLowerCase()
    .replace(/\(\s*\d+\s*\)/g, " ")
    .replace(/°\s*[fc]\b/g, " ")
    .replace(/\b(temperature|temp|humidity|humid|rh|sensor|probe)\b/g, " ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
  return key || null;
};

const titleCase = (s) => String(s || "").replace(/\b\w/g, (m) => m.toUpperCase());

// -> [{ key, label, icon, items: [{ type: "channel", channel } | { type: "zone", key, label, members }] }]
export const groupChannels = (models = []) => {
  const byGroup = {};
  models.forEach((c) => {
    (byGroup[c.group] = byGroup[c.group] || []).push(c);
  });

  return GROUP_ORDER.filter((g) => byGroup[g]?.length).map((g) => {
    const members = byGroup[g];
    let items = members.map((channel) => ({ type: "channel", channel }));

    if (g === "environment") {
      const buckets = {};
      members.forEach((c) => {
        if (c.zone) (buckets[c.zone] = buckets[c.zone] || []).push(c);
      });
      const placed = new Set();
      items = [];
      members.forEach((c) => {
        const bucket = c.zone ? buckets[c.zone] : null;
        if (bucket && bucket.length >= 2) {
          if (!placed.has(c.zone)) {
            placed.add(c.zone);
            items.push({
              type: "zone",
              key: `zone-${c.zone}`,
              label: c.zoneLabel || titleCase(c.zone),
              members: bucket,
            });
          }
        } else {
          items.push({ type: "channel", channel: c });
        }
      });
    }

    return { ...CHANNEL_GROUPS[g], items, count: members.length };
  });
};

// ------------------------------------------------------- filter + summary

export const filterChannels = (models = [], { filter = "all", query = "" } = {}) => {
  const q = String(query || "").toLowerCase().trim();
  return models.filter((c) => {
    if (filter === "attention" && !needsAttention(c)) return false;
    if (filter !== "all" && filter !== "attention" && c.group !== filter) return false;
    if (!q) return true;
    return [c.name, c.kindLabel, `#${c.number}`, String(c.number), c.stateLabel]
      .filter(Boolean)
      .some((s) => String(s).toLowerCase().includes(q));
  });
};

export const summarizeChannels = (models = []) => {
  const tanks = models.filter((c) => c.kind === "level" && c.value !== null);
  const collection = tanks.filter((c) => c.role === "collection");
  const supply = tanks.filter((c) => c.role === "supply");
  const temps = models.filter((c) => c.kind === "temperature" && c.value !== null);

  const fullestCollection = collection.reduce((best, c) => (!best || c.value > best.value ? c : best), null);
  const lowestSupply = supply.reduce((best, c) => (!best || c.value < best.value ? c : best), null);
  const collectionVolume = collection.reduce((sum, c) => sum + (c.volume || 0), 0);

  return {
    total: models.length,
    attention: models.filter(needsAttention).length,
    alarms: models.filter((c) => c.status.level === "alarm").length,
    fullestCollection,
    lowestSupply,
    collectionVolume: collection.some((c) => c.volume !== null) ? collectionVolume : null,
    volumeUnit: collection.find((c) => c.volumeUnit)?.volumeUnit || "",
    tempRange: temps.length
      ? {
          min: Math.min(...temps.map((c) => c.value)),
          max: Math.max(...temps.map((c) => c.value)),
          unit: temps[0].unit,
        }
      : null,
  };
};

// ------------------------------------------------------------- formatting

const NO_SPACE_UNITS = ["%", "°F", "°C", "°", "%RH"];

export const formatNumber = (v, digits) => {
  if (v === null || v === undefined || !Number.isFinite(v)) return "—";
  const d = digits ?? (Math.abs(v) >= 100 ? 0 : 1);
  return Number(v.toFixed(d)).toLocaleString(undefined, { maximumFractionDigits: d });
};

export const formatChannelValue = (value, unit = "", kind) => {
  if (value === null || value === undefined) return "—";
  const n = kind === "level" ? formatNumber(value, 0) : formatNumber(value);
  if (!unit) return n;
  return NO_SPACE_UNITS.includes(unit) ? `${n}${unit}` : `${n} ${unit}`;
};

// Delta text stays neutral; direction is shown with an arrow (rising is not "bad" for every tank).
export const formatDelta = (delta, unit = "") => {
  if (delta === null || delta === undefined || !Number.isFinite(delta)) return null;
  if (Math.abs(delta) < 0.05) return { text: "± 0", direction: "flat" };
  const arrow = delta > 0 ? "▲" : "▼";
  return {
    text: `${arrow} ${formatChannelValue(Math.abs(delta), unit === "%" ? "%" : unit)}`,
    direction: delta > 0 ? "up" : "down",
  };
};

export const formatHours = (h) => {
  if (h === null || h === undefined || !Number.isFinite(h)) return "—";
  if (h < 1) return `${Math.max(1, Math.round(h * 60))} min`;
  if (h < 48) return `${h.toFixed(1)} h`;
  return `${(h / 24).toFixed(1)} days`;
};

// ------------------------------------------------------------- normalize

const listChannelNumber = (c, i) => String(c?.channel_number ?? c?.channel ?? i + 1);

export const normalizeChannels = ({
  channelsList = [],
  liveChannels,
  thresholds = [],
  paramValues = [],
  analysisData = {},
  alertsByChannel = {},
  now = Date.now(),
} = {}) => {
  const live = Object.fromEntries(toChannelEntries(liveChannels));
  const listByKey = {};
  const order = [];
  (channelsList || []).forEach((c, i) => {
    const key = listChannelNumber(c, i);
    if (listByKey[key]) return;
    listByKey[key] = c;
    order.push(key);
  });
  Object.keys(live).forEach((key) => {
    if (!listByKey[key]) order.push(key);
  });

  const kindByKey = {};
  order.forEach((key) => {
    kindByKey[key] = resolveChannelKind(live[key] || {}, listByKey[key]?.channel_type);
  });

  const normalizedThresholds = normalizeThresholds(thresholds);
  const history = buildHistoryIndex(paramValues, (key, raw) => kindByKey[key] || resolveChannelKind(raw));

  return order.map((key) => {
    const raw = live[key] || {};
    const meta = listByKey[key] || {};
    const kind = kindByKey[key];
    const kindDef = CHANNEL_KINDS[kind] || CHANNEL_KINDS.other;
    const name = meta.channel_name || raw.channel_name || `Channel ${key}`;
    const value = readChannelValue(raw, kind);
    const unit = readChannelUnit(raw, kind);
    const channelThresholds = normalizedThresholds.filter((t) => t.channelKey === key);

    const volume = toNumber(raw.volume_level);
    const explicitCapacity = toNumber(raw.capacity ?? raw.calibration?.capacity ?? meta.capacity);
    const capacity =
      explicitCapacity ?? (volume !== null && value > 0 && kind === "level" ? volume / (value / 100) : null);
    const fluidHeight = toNumber(raw.actual_fluid_height);
    const usableHeight = toNumber(raw.usable_height);
    const updatedAt = toTimestamp(raw.timestamp ?? raw.updated_at ?? raw.created_at);

    const points = [...(history[key] || [])];
    if (value !== null) {
      const t = updatedAt ?? now;
      const lastT = points.length ? points[points.length - 1].t : -Infinity;
      if (t > lastT) points.push({ t, v: value });
    }
    const points24 = windowPoints(points, 24, now);
    const stats24 = seriesStats(points24);

    const analysisPct = toNumber(analysisData?.[key]?.last_24_hours?.percent_change);
    const delta24 =
      kind === "level" && analysisPct !== null
        ? analysisPct
        : stats24 && stats24.count > 1
        ? stats24.last.v - stats24.first.v
        : null;

    const status = evaluateChannelStatus({
      value,
      fluidHeight,
      thresholds: channelThresholds,
      activeAlerts: alertsByChannel?.[key] || [],
    });

    const zoneSource = meta.zone || null;
    const zone = kindDef.group === "environment" ? zoneKeyFor(name, zoneSource) : null;

    return {
      key,
      number: Number.isFinite(Number(key)) ? Number(key) : key,
      name,
      kind,
      kindLabel: kindDef.label,
      group: kindDef.group,
      icon: kindDef.icon,
      value,
      unit,
      volume,
      volumeUnit: raw.volume_unit || raw.calibration?.volume_unit || (volume !== null ? "gal" : ""),
      capacity,
      ullage: capacity !== null && volume !== null ? Math.max(0, capacity - volume) : null,
      fluidHeight,
      usableHeight,
      heightUnit: raw.height_unit || raw.calibration?.scale_unit || "in",
      rangeMax: toNumber(raw?.state?.range_max),
      updatedAt,
      role: kind === "level" ? resolveTankRole({ name, thresholds: channelThresholds, override: meta.role }) : null,
      zone,
      zoneLabel: zoneSource ? String(zoneSource) : null,
      stateLabel: raw?.state?.display_label || null,
      stateColor: raw?.state?.display_color || null,
      thresholds: channelThresholds,
      status,
      points,
      points24,
      stats24,
      delta24,
      forecast: forecastNextThreshold({ value, points24, thresholds: channelThresholds }),
      raw,
    };
  });
};
